import type {
  ProductListingFilters,
  ProductListingApiResponse,
  Product,
  PriceUpdate,
  StockUpdateItem,
  StockUpdateResponse,
  Photo, // Import Photo type
  MarketplaceShipperApiResponse,
} from '../types/ProductListing'
import type { ProductListingDetail } from '../types/ProductListingDetail'
import type { IProductListingService, ProductListingRequest } from './IProductListingService'
import type { AccountCategory } from '../../category/types/AccountCategory'; // Import AccountCategory type
import * as AccountService from "src/db/serviceAccount"
import * as lc from "src/app/modules/localstorage/index"
import { HttpClient } from 'src/app/modules/core'

class ProductListingService implements IProductListingService {
  private static instance: ProductListingService
  private http: HttpClient

  constructor() {
    this.http = HttpClient.getInstance();
  }

  static getInstance(): ProductListingService {
    if (!this.instance) {
      this.instance = new ProductListingService();
    }
    return this.instance;
  }

  private normalizeBoolean(value: any): boolean {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
      const normalized = value.toLowerCase().trim();
      return normalized === 'true' || normalized === '1';
    }
    if (typeof value === 'number') return value === 1;
    return false;
  }

  async getProductListingDetails(productId: string): Promise<ProductListingDetail> {
    try {
      const result = await this.http.get<any>(
        `/api/v2/product_listings/${productId}`,
        {},
        { useCache: false }
      );

      if (!result.success || !result.data) {
        throw new Error(result.error || `Failed to fetch product details for ID: ${productId}`);
      }

      console.log("[DEBUG] Raw API Response:", result.data);

      const transformedProduct = this.transformApiProduct(result.data.data);

      console.log("[DEBUG] Transformed (camelCase) Product:", transformedProduct);

      const apiData = result.data.data;

      // Extract attribute values from attribute_list - will be mapped to category schema later
      const savedAttributeValues = this.extractAttributeValues(apiData.attribute_list || []);

      return {
        ...transformedProduct,
        attributes: [],  // Empty - will be populated by category mapping
        savedAttributeValues,  // Raw saved values for mapping
        variantOptions: apiData.variant_options || [],
        attributeList: apiData.attribute_list || [],  // Store raw structure
        isDangerousProduct: apiData.is_dangerous_product,
        statusNote: apiData.status_note,
        dimension: apiData.dimension,
        weight: apiData.weight,
      };
    } catch (error) {
      console.error(`Error fetching product details for ID ${productId}:`, error);
      throw error;
    }
  }

  private transformApiProduct(apiProduct: any): Product {
    // Helper to ensure boolean values from API (e.g., '1', 'true', 1) are normalized
    const normalizeBoolean = (value: any): boolean => {
      if (typeof value === 'boolean') return value;
      if (typeof value === 'string') {
        const normalized = value.toLowerCase().trim();
        return normalized === 'true' || normalized === '1';
      }
      if (typeof value === 'number') return value === 1;
      return false;
    };

    const photos: Photo[] = (apiProduct.photos || []).map((photo: any) => ({
      url: typeof photo === 'string' ? photo : photo.url, // Handle string URLs or objects
      is_primary: normalizeBoolean(photo.is_primary),
    }));

    let mainImageFromPhotos: string = '';
    photos.forEach((photo: Photo) => {
      if (photo.is_primary) {
        mainImageFromPhotos = photo.url;
      }
    });

    const mainImage: string = mainImageFromPhotos || apiProduct.main_image || (photos.length > 0 ? photos[0].url : '');

    return {
      id: apiProduct.id,
      sku: apiProduct.sku,
      productListingId: apiProduct.listing_id,
      accountId: apiProduct.account_id,
      marketplace: apiProduct.marketplace,
      title: apiProduct.title,
      description: apiProduct.description,
      brandName: apiProduct.brand_name,
      status: apiProduct.status,
      statusNote: apiProduct.status_note || "",
      isActive: normalizeBoolean(apiProduct.is_active),
      price: apiProduct.price
        ? {
            current: apiProduct.price.current || 0,
            original: apiProduct.price.original || 0,
            currency: apiProduct.price.currency || "IDR",
          }
        : {
            current: 0,
            original: 0,
            currency: "IDR",
          },
      inventoryAllocation: apiProduct.inventory_allocation || {},
      totalStock: apiProduct.total_stock || 0,
      mainImage: mainImage,
      photos: photos,
      category: (apiProduct.category || []).map((cat: any) => ({
        id: cat.id,
        name: cat.name,
        hasChildren: normalizeBoolean(cat.has_children),
        isActive: normalizeBoolean(cat.is_active),
        parentCategoryId: cat.parent_category_id || null,
      })),
      ratings: apiProduct.ratings
        ? {
            avgRating: apiProduct.ratings.avg_rating || 0,
            totalRatingCount: apiProduct.ratings.total_rating_count || 0,
          }
        : {
            avgRating: 0,
            totalRatingCount: 0,
          },
      salesMetrics: apiProduct.sales_metrics
        ? {
            salesQty30d: apiProduct.sales_metrics.sales_qty_30d || 0,
            salesRevenue30d: apiProduct.sales_metrics.sales_revenue_30d || 0,
            avgDailySalesQty30d:
              apiProduct.sales_metrics.avg_daily_sales_qty_30d || 0,
          }
        : {
            salesQty30d: 0,
            salesRevenue30d: 0,
            avgDailySalesQty30d: 0,
          },
      sync: apiProduct.sync
        ? {
            status: apiProduct.sync.status || "pending",
            lastSynced: apiProduct.sync.last_synced,
            nextSyncAt: apiProduct.sync.next_sync_at,
          }
        : {
            status: "pending",
            lastSynced: undefined,
            nextSyncAt: undefined,
          },
      createdAt: apiProduct.created_at,
      updatedAt: apiProduct.updated_at,
      variantCombinations:
        apiProduct.variant_combinations?.map((variant: any) => ({
          id: variant.id,
          sku: variant.sku,
          variantId: variant.listing_variant_id,
          stock: variant.stock || 0,
          photo: variant.photo, // This is likely a string URL
          weight: variant.weight,
          dimension: variant.dimension
            ? {
                height: variant.dimension.height || 0,
                length: variant.dimension.length || 0,
                width: variant.dimension.width || 0,
              }
            : {
                height: 0,
                length: 0,
                width: 0,
              },
          salesMetrics: variant.sales_metrics
            ? {
                salesQty30d: variant.sales_metrics.sales_qty_30d || 0,
                salesRevenue30d: variant.sales_metrics.sales_revenue_30d || 0,
              }
            : {
                salesQty30d: 0,
                salesRevenue30d: 0,
              },
          price: variant.price
            ? {
                current: variant.price.current || 0,
                original: variant.price.original || 0,
                currency: variant.price.currency || "IDR",
              }
            : {
                current: 0,
                original: 0,
                currency: "IDR",
              },
          isActive: normalizeBoolean(variant.is_active),
          isMainVariant: normalizeBoolean(variant.is_main_variant),
          tierIndex: variant.tier_index || [],
          tierVariation: variant.tier_variation || [],
          inventoryAllocation: variant.inventory_allocation || {},
          isBundle: normalizeBoolean(variant.is_bundle),
          merchantProduct: variant.merchant_product
            ? {
                id: variant.merchant_product.id,
                sku: variant.merchant_product.sku,
                name: variant.merchant_product.name,
              }
            : undefined,
        })) || [],
      merchantProduct: apiProduct.merchant_product
        ? {
            id: apiProduct.merchant_product.id,
            sku: apiProduct.merchant_product.sku,
            name: apiProduct.merchant_product.name,
          }
        : undefined,
      shippers: apiProduct.shippers || [],
    };
  }

  private extractAttributeValues(attributeList: any[]): Record<string, any> {
    // Convert attribute_list to simple key-value map
    return attributeList.reduce((acc, attr) => {
      // Extract first value from attribute_value_list
      const firstValue = attr.attribute_value_list?.[0];
      acc[attr.attribute_name] = firstValue?.value_name || "";
      return acc;
    }, {} as Record<string, any>);
  }

  private async getAccountIdValue(
    filters: ProductListingFilters
  ): Promise<string | string[] | null> {
    if (!filters.stores || filters.stores.length === 0) {
      return null;
    }

    try {
      const client = lc.getItemLC(lc.LCName.Client);
      const companyAccess = lc.getItemLC(lc.LCName.CompanyList);

      if (client?.id && companyAccess) {
        const accounts = await AccountService.getAccessibleAccount(
          client.id,
          companyAccess
        );
        const totalStores = accounts?.length || 0;

        if (filters.stores.length === totalStores) {
          return "all";
        }
      }
    } catch (error) {
      console.error("Error checking total stores for All Stores logic:", error);
    }
    return filters.stores.length === 1 ? filters.stores[0] : filters.stores;
  }

  async getProductListings(
    params: ProductListingRequest
  ): Promise<ProductListingApiResponse> {
    try {
      const result = await this.http.get<any>(
        "/api/v2/product_listings",
        params,
        { useCache: false }
      );

      if (!result.success) {
        console.error("Failed to fetch product listings:", result.error);
        throw new Error(result.error || "Failed to fetch product listings");
      }

      const responseData = result.data;

      return {
        success: true,
        data: {
          productListings: (responseData.data || []).map((product: any) =>
            this.transformApiProduct(product)
          ),
          pagination: {
            page: responseData.pagination?.page || 1,
            pageSize:
              responseData.pagination?.page_size || params.page_size || 20,
            totalCount: responseData.pagination?.total_count || 0,
            totalPages:
              responseData.pagination?.total_pages ||
              Math.ceil(
                (responseData.pagination?.total_count || 0) /
                  (responseData.pagination?.page_size || params.page_size || 20)
              ),
            hasNext: responseData.pagination?.has_next || false,
            hasPrevious: responseData.pagination?.has_previous || false,
          },
        },
      };
    } catch (error) {
      console.error("Error fetching product listings:", error);
      throw new Error("Failed to fetch product listings");
    }
  }

  async getProductListingsByFilters(
    filters: ProductListingFilters
  ): Promise<ProductListingApiResponse> {
    const accountIdValue = await this.getAccountIdValue(filters);

    if (accountIdValue === null) {
      return {
        success: true,
        data: {
          productListings: [],
          pagination: {
            page: 1,
            pageSize: 20,
            totalCount: 0,
            totalPages: 0,
            hasNext: false,
            hasPrevious: false,
          },
        },
      };
    }

    const params: ProductListingRequest = {
      account_id: accountIdValue,
      search: filters.search || undefined,
      status: filters.status !== "all" ? filters.status : undefined,
      marketplace: undefined,
    };

    return this.getProductListings(params);
  }

  async getProductListingsWithPagination(
    filters: ProductListingFilters,
    page: number,
    pageSize: number
  ): Promise<ProductListingApiResponse> {
    const accountIdValue = await this.getAccountIdValue(filters);

    if (accountIdValue === null) {
      return {
        success: true,
        data: {
          productListings: [],
          pagination: {
            page,
            pageSize,
            totalCount: 0,
            totalPages: 0,
            hasNext: false,
            hasPrevious: false,
          },
        },
      };
    }

    const params: ProductListingRequest = {
      account_id: accountIdValue,
      page,
      page_size: pageSize,
      search: filters.search || undefined,
      status: filters.status !== "all" ? filters.status : undefined,
      marketplace: undefined,
    };

    return this.getProductListings(params);
  }

  /**
   * Update product stock in bulk across multiple warehouses
   */
  async updateProductStock(
    accountId: string,
    updates: StockUpdateItem[]
  ): Promise<StockUpdateResponse> {
    try {
      if (updates.length > 1000) {
        throw new Error('Maximum 1,000 updates allowed per batch')
      }

      const invalidStock = updates.find(
        (u) => u.new_ready_stock < 0 || u.new_ready_stock > 999999
      )
      if (invalidStock) {
        throw new Error(
          `Invalid stock quantity ${invalidStock.new_ready_stock}. Must be between 0 and 999,999`
        )
      }

      const uniqueKeys = new Set<string>()
      for (const update of updates) {
        // Validate that either sku OR listing_id is provided (not both)
        if (!update.sku && !update.listing_id) {
          throw new Error(
            `Invalid stock update: Must provide either 'sku' or 'listing_id'`
          )
        }
        if (update.sku && update.listing_id) {
          throw new Error(
            `Invalid stock update: Cannot provide both 'sku' and 'listing_id'`
          )
        }

        // Validate variant fields: if provided, only one should be present
        // For single-variant products, both can be omitted
        if (update.variant_sku && update.listing_variant_id) {
          throw new Error(
            `Invalid stock update: Cannot provide both 'variant_sku' and 'listing_variant_id'`
          )
        }

        // Validate warehouse_id is present
        if (!update.warehouse_id) {
          throw new Error(
            `Invalid stock update: Missing warehouse_id`
          )
        }

        const productKey = update.sku || update.listing_id
        const variantKey = update.variant_sku || update.listing_variant_id || 'single'
        const key = `${productKey}:${variantKey}:${update.warehouse_id}`
        if (uniqueKeys.has(key)) {
          throw new Error(
            `Duplicate update found for Product: ${productKey}, Variant: ${variantKey}, Warehouse: ${update.warehouse_id}`
          )
        }
        uniqueKeys.add(key)
      }

      const result = await this.http.post<StockUpdateResponse>(
        '/api/v2/product_listing/stock',
        {
          account_id: accountId,
          updates: updates,
        }
      )

      if (!result.success || !result.data) {
        const errorMessage = result.error || 'Failed to update product stock'
        console.error('[ProductListingService] Stock update error:', errorMessage)
        throw new Error(errorMessage)
      }

      return result.data
    } catch (error) {
      console.error('[ProductListingService] Exception in updateProductStock:', error)
      throw error
    }
  }

  /**
   * Update product price
   */
  async updateProductPrice(
    productId: string,
    priceUpdates: PriceUpdate[]
  ): Promise<{ success: boolean; message?: string }> {
    console.warn('[ProductListingService] updateProductPrice is deprecated. Use PriceUpdateService instead.')

    return {
      success: false,
      message: 'This method is deprecated. Use PriceUpdateService.updateProductPrices() instead.'
    }
  }

  /**
   * Get all available marketplace shippers for a specific marketplace
   * Uses HttpClient caching for performance
   */
  async getMarketplaceShippers(marketplace: string): Promise<MarketplaceShipperApiResponse> {
    try {
      const result = await this.http.get<any>(
        `/api/v2/shipper/${marketplace}/marketplace-shipments`,
        {},
        { useCache: true }  // Cache this as available shippers don't change often
      );

      if (!result.success || !result.data) {
        throw new Error(result.error || `Failed to fetch marketplace shippers for ${marketplace}`);
      }

      console.log('[ProductListingService] Marketplace shippers full response:', result.data);
      console.log('[ProductListingService] Shipments array:', result.data.data?.shipments);

      // Return the nested data.shipments structure
      return result.data.data || { shipments: [] };
    } catch (error) {
      console.error(`[ProductListingService] Error fetching marketplace shippers for ${marketplace}:`, error);
      throw error;
    }
  }

  /**
   * Clear cached product listing data for an account
   */
  clearProductListingCache(accountId?: string): void {
    this.http.clearCache('/api/v2/product_listings')

    console.log('[ProductListingService] Cleared product listing cache', accountId ? `for account ${accountId}` : 'globally')
  }
}

export default ProductListingService;